/**
 * SRAC Upcoming Events Card
 *
 * Renders the home page's "Upcoming Events" card, listing the next three
 * club events with their date, time, and colour-coded event type. Reads them
 * with a bounded Firestore query (today onward, ordered by date, limited to
 * three) so a home-page view bills only the events it shows rather than the
 * whole events collection.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, ArrowRight, Loader2 } from 'lucide-react';
import { format, isFuture } from 'date-fns';
import Link from 'next/link';
import { useFirestore, useCollection } from '@/firebase';
import { collection, query, orderBy, where, limit, Query } from 'firebase/firestore';
import type { Event as FirebaseEvent, EventType } from '@/models/event';

import { parseEventDate } from '@/lib/date-utils';

export default function EventsCard({ className }: { className?: string }) {
  const firestore = useFirestore();

  // Today's date in YYYY-MM-DD format, matching the `events.date` string
  // format so it can be compared lexically both here and server-side.
  const today = format(new Date(), 'yyyy-MM-dd');

  // Fetch events and event types from Firebase. The date/limit constraints
  // push the filtering below down into Firestore so only the three rendered
  // events are billed as reads.
  const eventsQuery = useMemo(() => {
    return query(
      collection(firestore, 'events'),
      where('date', '>=', today),
      orderBy('date', 'asc'),
      limit(3),
    ) as Query<FirebaseEvent>;
  }, [firestore, today]);

  const eventTypesQuery = useMemo(() => {
    return query(collection(firestore, 'eventTypes')) as Query<EventType>;
  }, [firestore]);

  const { data: events, loading: eventsLoading } = useCollection<FirebaseEvent>(eventsQuery);
  const { data: eventTypes, loading: typesLoading } = useCollection<EventType>(eventTypesQuery);

  const upcomingEvents = useMemo(() => {
    if (!events) return [];

    return events
      .filter((event) => {
        // Compare date strings directly (YYYY-MM-DD >= YYYY-MM-DD)
        return event.date.split('T')[0] >= today;
      })
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 3);
  }, [events, today]);

  const getEventTypeName = (typeId: string) => {
    return eventTypes?.find(t => t.id === typeId)?.name || '';
  };

  const getEventTypeColor = (typeId: string): string => {
    const eventType = eventTypes?.find(t => t.id === typeId);
    if (eventType?.color) {
      return eventType.color;
    }
    return '#6B7280'; // Gray fallback
  };

  const loading = eventsLoading || typesLoading;

  return (
    <Card className={`${className} flex flex-col`} id="events">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary" />
          Upcoming Events
        </CardTitle>
        <CardDescription>
          A quick look at the next few events on our calendar.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-muted-foreground">Loading events...</span>
          </div>
        ) : upcomingEvents.length > 0 ? (
          upcomingEvents.map((event) => {
            const typeColor = getEventTypeColor(event.typeId);
            const eventDate = parseEventDate(event.date);
            return (
              <div
                key={event.id}
                className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-secondary transition-colors"
              >
                <div className="flex-shrink-0 text-center w-16">
                  <p className="text-sm font-semibold text-primary">
                    {format(eventDate, 'MMM')}
                  </p>
                  <p className="text-2xl font-bold">
                    {format(eventDate, 'dd')}
                  </p>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{event.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {format(eventDate, 'eeee')}
                    {event.time ? `, ${event.time}` : ''}
                  </p>
                  {eventTypes && (
                    <span
                      className="inline-block text-xs px-2 py-0.5 rounded-full text-white font-medium mt-1"
                      style={{ backgroundColor: typeColor }}
                    >
                      {getEventTypeName(event.typeId)}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">No upcoming events scheduled.</p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button asChild className="w-full" variant="outline">
          <Link href="/events">
            View Full Calendar
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
